/**
 * Customer Revisions — prototype `vRevision()`, contract §9, infra C.7.
 *
 * What happens after a quotation is sent: the customer accepts, asks for a better rate, or
 * adds/changes/drops items. Each request is resolved into a change that lands on the
 * quotation, bumping its revision and returning it to draft for re-issue.
 *
 * FE/BE split (infra C.7): ranking suppliers "history first" for an added item is the
 * client's, from the purchase-history payload — applying the change and re-pricing is the
 * server's.
 */

import { useCompanyBlanketOrders } from '../../lib/api/useCompanyBlanketOrders'
import { useEffect, useMemo, useState } from 'react'
import { useBlocker, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { ReadOnlyGate } from '../../components/ReadOnlyGate'
import { lockReason } from '../../lib/readOnly'
import { ContextBar } from '../../components/ContextBar'
import { Button, PageHead, Placeholder } from '../../components/ui'
import { Badge } from '../../components/Badge'
import { useToast } from '../../components/Toast'
import { useEnquiry } from '../../lib/api/useEnquiries'
import { useBlanketOrderItems, useItemsByCode } from '../../lib/api/useItems'
import { buildRateIndex, contractsForItem } from '../../lib/blanketOrders'
import { useExistingQuotation, useSetLineCosts, useUpdateQuotationLine, useUpdateQuotationLineFields } from '../../lib/api/useQuotation'
import { useApplyRevisions, useRevisions } from '../../lib/api/useRevisions'
import { useQuotationHistory } from '../../lib/api/useQuotationHistory'
import { useQuotationSends } from '../../lib/api/useQuotationSends'
import { toSentRounds } from '../../lib/quotationHistory'
import { stillOpen } from '../../lib/revisions'
import { FX_FALLBACK } from '../../lib/fx'
import {
  describeBatch,
  discardEdit,
  editFromRequest,
  describedAddsFor,
  positionOptions,
  emptyBatch,
  addBlockedReason,
  linesForItemCode,
  prevailingSellRatio,
  pricedAddsFor,
  queueEdit,
  toChanges,
} from '../../lib/revisionBatch'
import { QuotationEditor } from '../quote/QuotationEditor'
import { RevisionHistory } from './RevisionHistory'
import { RevisionRequestsTable } from './RevisionRequestsTable'
import { PendingChangesCard } from './PendingChangesCard'
import { SupplierChoiceModal } from './SupplierChoiceModal'
import { RecordQuotationModal } from '../suppliers/RecordQuotationModal'
import { buildManualQuotation } from '../../lib/manualSupplierQuotation'
import { lineForRevisionRequest, planRfqSourcing } from '../../lib/revisionQuoteLine'
import { baseCurrencyFor } from '../../lib/api/baseCurrency'
import { useExchangeRates } from '../../lib/api/useMasters'
import { pickRate } from '../../lib/exchangeRates'
import { useCreateSupplierQuotation } from '../../lib/api/useSupplierQuotations'
import { useAddLine } from '../../lib/api/useEnquiryMutations'
import { CatalogPickerModal } from '../rfq/CatalogPickerModal'
import type { PendingEdit } from '../../lib/revisionBatch'
import type { RevisionRequest } from '../../lib/types'

export function RevisionsPage() {
  const { id = '' } = useParams()
  const navigate = useNavigate()
  const toast = useToast()

  const [addingFor, setAddingFor] = useState<RevisionRequest | null>(null)
  /**
   * The revision request we are recording a supplier's price for, or `null`.
   *
   * Holds the REQUEST, not a boolean: the modal has to be seeded with the line for that specific
   * item. Opened with the enquiry's whole line list it showed every RFQ line — or, when the
   * requested item is not on the enquiry, none at all with a message about matching items that had
   * nothing to do with what the purchaser was doing.
   */
  const [recordingQuote, setRecordingQuote] = useState<RevisionRequest | null>(null)
  /*
    Putting the requested item on the ENQUIRY, which is the prerequisite for sending an RFQ for it
    — see `planRfqSourcing`. Errors are surfaced and the navigation abandoned: landing on the RFQ
    builder after a failed add is the same dead end this replaced.
  */
  const addLine = useAddLine(id, {
    onError: (error) => toast(`Could not add the item to the enquiry: ${error.message}`),
  })
  const createSupplierQuotation = useCreateSupplierQuotation({
    onSuccess: () => toast('Supplier quotation recorded — its price is now available to this item.'),
    onError: (error) => toast(`Could not record it: ${error.message}`),
  })
  /** Open when adding a line the customer didn't formally request (no revision request). */
  const [pickingFromMaster, setPickingFromMaster] = useState(false)
  const [busyRequestId, setBusyRequestId] = useState<string | null>(null)

  /*
    Structural edits (qty/add/remove) made directly on the embedded quotation, below. These do
    NOT apply themselves — `apply_revisions` bumps the revision on every call, so N edits made
    here collect into this one batch and go out as a SINGLE call when the purchaser hits Apply.
    See `lib/revisionBatch.ts`'s own header for why.
  */
  const [batch, setBatch] = useState<PendingEdit[]>(emptyBatch())
  /*
    The unit and line number the NEXT add takes (Akhil, 2026-09-15). Reset for each item the
    dialog opens on: unit to the item's base (or what the request named), number to the next free
    one — the purchaser changes either in the dialog, and every price taken there queues the add
    with both (`queueGuarded` merges them).
  */
  const [addChoice, setAddChoice] = useState<{ uom: string; position: string }>({ uom: '', position: '' })
  const openAddDialog = (request: RevisionRequest) => {
    const item = request.item_code ? itemsByCode.get(request.item_code) : undefined
    const positions = positionOptions(quotation?.lines ?? [], batch)
    setAddChoice({
      uom: item?.base_uom ?? '',
      // A freed number first when there is one — replacing is the common reason to add.
      position: positions[0]?.value ?? '',
    })
    setAddingFor(request)
  }

  const pending = useMemo(() => new Map(batch.map((e) => [e.item_code, { op: e.op, qty: e.qty }])), [batch])

  /*
    Batching means these edits exist ONLY on screen until applied. Losing them silently to a
    stray tab close is worse than the extra revision the batching was avoiding.
  */
  useEffect(() => {
    if (batch.length === 0) return
    const warn = (e: BeforeUnloadEvent) => e.preventDefault()
    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [batch.length])

  /*
    `beforeunload` only covers closing the TAB. Clicking "View quotation", a nav link, or any
    other in-app route change is the far more likely way to lose a queued batch, and it never
    fires that event — the router just swaps components. `useBlocker` (stable since this data
    router) intercepts navigation within the app the same way; a confirm is enough friction to
    stop an accidental click without a bespoke modal for a screen this narrow.
  */
  const blocker = useBlocker(batch.length > 0)
  useEffect(() => {
    if (blocker.state !== 'blocked') return
    if (window.confirm('You have unapplied changes on this quotation. Leave without applying them?')) {
      blocker.proceed()
    } else {
      blocker.reset()
    }
  }, [blocker])

  const enquiryQuery = useEnquiry(id)
  // `useExistingQuotation` still returns `null` instead of throwing when nothing exists yet
  // (this screen's own "no quotation yet" state below), but it can no longer avoid CALLING
  // `comparison.build_quotation` to resolve the name — there is no other enquiry→quotation
  // lookup on this API. See that hook's own header in `useQuotation.ts` for the full story and
  // task-11-report.md for the open question it leaves (is `build_quotation` really a no-op once
  // a quotation exists, sent or not?).
  const quotationQuery = useExistingQuotation(id)
  const quotation = quotationQuery.data
  // For pricing a revision add whose source is in another currency — see the apply handler.
  const exchangeRates = useExchangeRates()
  const historyQuery = useQuotationHistory(quotation?.id)
  const sendsQuery = useQuotationSends(quotation?.id)
  /*
    Grouped by what actually went OUT, not by every save. `Version` records the edits and
    `Email Queue` records the issues; neither alone answers "what did the customer get".
  */
  const sentHistory = useMemo(
    () => toSentRounds(historyQuery.data ?? [], sendsQuery.data ?? []),
    [historyQuery.data, sendsQuery.data],
  )
  const revisionsQuery = useRevisions(quotation?.id)

  const requests = revisionsQuery.data?.requests ?? []
  /*
    Requests the purchaser has satisfied HERE, in this session.

    A reprice is applied through `update_quotation_line`, which never touches the request, and no
    API exists to close one — so without this the send gate refused the re-issue for ever, to
    somebody who had just done what the customer asked. See `stillOpen`.
  */
  const [resolvedHere, setResolvedHere] = useState<Set<string>>(new Set())
  /*
    A rate was changed since the last bump.

    `update_quotation_line` bumps nothing, so a round of PURE price edits produced no revision and
    nothing to apply — the Apply button never appeared, because it keyed on the structural batch
    alone. This makes a reprice-only round applicable, and `apply_revisions` is then called ONCE
    for the whole round (with `changes: []` when nothing structural was queued — verified live,
    revision 10 → 11). One round of customer corrections is one revision, whether it changed
    quantities, prices or both.
  */
  // `?repriced=1` — arriving from the comparison page, which has just re-priced lines on the sent
  // quotation as a revision (2026-09-17); the round is offered for applying at once.
  const [params] = useSearchParams()
  const [repriced, setRepriced] = useState(() => params.get('repriced') === '1')
  const open = stillOpen(requests, resolvedHere)

  /*
    Every code this screen needs a catalogue record for — NOT just the revision requests'.

    `addingFor` is also synthesized by `CatalogPickerModal` for an item the purchaser picked
    themselves, and that code is on no request. Fetching only the requests' codes left
    `itemsByCode` permanently missing it, so "Send an RFQ" refused with "still loading …" for an
    item that was never being loaded (reported 2026-09-04 against `790894`). `recordingQuote` is
    included for the same reason — it supplies the UOM the recorded line is priced in.
  */
  const itemCodes = useMemo(
    () => [
      ...new Set(
        [...requests.map((r) => r.item_code), addingFor?.item_code, recordingQuote?.item_code].filter(
          (c): c is string => !!c,
        ),
      ),
    ],
    [requests, addingFor, recordingQuote],
  )
  const { byCode: itemsByCode } = useItemsByCode(itemCodes)

  /*
    Active blanket orders, so an item the customer asks to ADD can be priced from a contract that
    already fixes its rate — the RFQ builder's route, which this screen did not offer at all.

    It is the FIRST thing to check, not the last: a contract rate is already agreed, so there is
    nothing to quote and nobody to wait for. Measured 2026-09-04 — VEG EGGPLANT (`0002060047`) is
    on Fruits Garden Trading LLC's contract at AED 4.85, and reached this quotation at 0.00 because
    nothing here looked.

    Whole-list, not per item: the five active contracts arrive with their 420 rates INLINE
    (`useActiveBlanketOrders`' own note), so this is one request for every item the screen will ever
    ask about, and `useBlanketOrderItems` reads them straight from the cache it seeded.
  */
  // This enquiry's company's contracts only (2026-10-26).
  const activeOrders = useCompanyBlanketOrders(enquiryQuery.data?.header.company)
  const orderNames = useMemo(() => (activeOrders.data ?? []).map((o) => o.name), [activeOrders.data])
  const orderItems = useBlanketOrderItems(orderNames)
  const rateIndex = useMemo(() => buildRateIndex(orderItems.data ?? []), [orderItems.data])

  // The ONLY caller of `apply_revisions` on this screen — the batch-apply button below. Every
  // request the table resolves (add/set_qty/remove) queues into `batch` via `editFromRequest`
  // instead of calling this directly, so `busyRequestId`/`addingFor` are no longer this
  // mutation's business: it has exactly one call site and that site owns its own state.
  const applyRevisions = useApplyRevisions(id, quotation?.id, {
    onSuccess: (data) => toast(`Applied — quotation now revision ${data.revision}, back to draft`),
    onError: (error) => toast(`Could not apply: ${error.message}`),
  })

  /*
    Writes a recorded supplier price onto the line the apply just created — see `pricedAddsFor`.
    Its own toast, because it is a SECOND write: the apply can succeed and this still fail, and
    saying "applied" alone would leave the purchaser believing a line was priced when it is not.
  */
  const setLineCosts = useSetLineCosts(id, quotation?.id, {
    onError: (error) => toast(`Applied, but could not price the added line(s): ${error.message}`),
  })
  // The added line's description and name — see the apply handler.
  const describeLine = useUpdateQuotationLineFields(id, quotation?.id, {
    onError: (error) => toast(`Applied, but could not write the added line's description: ${error.message}`),
  })

  // `apply_revisions` has no price field at all (`RevisionChange`'s own comment, `../../lib/types`)
  // — a "reprice" request is resolved through the line's own PATCH instead.
  const updateLine = useUpdateQuotationLine(id, quotation?.id, {
    onError: (error) => {
      setBusyRequestId(null)
      toast(`Could not reprice: ${error.message}`)
    },
  })

  /*
    Re-issuing is a SEND, so it needs a real recipient for exactly the reason the quote screen
    does: `to: []` is falsy in Python and drops `send_quotation` into a lookup that dies on
    `Unknown column 'customer_primary_contact'` (`B8`). That is what "Could not re-issue:
    MySQLdb.OperationalError" was.
  */



  /** Every quotation line for a request's item — usually one, but never assumed. */
  const linesFor = (request: RevisionRequest) => quotation?.lines.filter((l) => l.item_code === request.item_code) ?? []

  const currentQtyOf = (request: RevisionRequest) => linesFor(request)[0]?.qty ?? null

  /**
   * Queues a structural edit, UNLESS `item_code` names more than one line on the quotation (fix
   * round, final review: I5). The batch — and the wire it goes out as — is keyed by `item_code`
   * alone (`lib/revisionBatch.ts`'s own header: `apply_revisions` resolves changes the same way),
   * so a qty/remove edit for a repeated code would land on every line sharing it at once. That is
   * exactly the ambiguity the reprice path already refuses (`matches.length > 1` above) — this is
   * the same guard for the queue path, which had none. Returns whether it queued, so a caller
   * that wants its own success toast only fires it when something actually happened.
   */
  const queueGuarded = (input: PendingEdit): boolean => {
    // An add carries the unit and line number chosen in the dialog — see `addChoice`.
    const edit: PendingEdit =
      input.op === 'add'
        ? { ...input, ...(addChoice.uom ? { uom: addChoice.uom } : {}), ...(addChoice.position ? { position: addChoice.position } : {}) }
        : input
    const matches = linesForItemCode(quotation?.lines ?? [], edit.item_code)
    if (matches.length > 1) {
      toast(
        `${edit.item_code} is on ${matches.length} lines (${matches.map((l) => l.line_id).join(', ')}) — qty/remove edits aren't supported for a repeated item_code.`,
      )
      return false
    }
    /*
      An ADD for an item already on the quotation appends a SECOND line — `apply_revisions` never
      merges. That is what put `1903010001` on two lines and left the newer one stuck at 0.00; see
      `addBlockedReason`.
    */
    if (edit.op === 'add') {
      const blocked = addBlockedReason(quotation?.lines ?? [], edit.item_code)
      if (blocked) {
        toast(blocked)
        return false
      }
    }
    setBatch((b) => queueEdit(b, edit))
    return true
  }

  /**
   * Turn a request into the change that resolves it. `apply_revisions` targets by `item_code`
   * now, not `line_id` (Task 11) — and carries no `request_id` to close a specific request with
   * (see `RevisionRequest`'s own comment in `../../lib/types`), so which open request this
   * satisfies is left for the SERVER to work out on its own.
   */
  const applyRequest = (request: RevisionRequest, newRate?: number) => {
    if (!request.item_code) {
      toast('This request has no item code — resolve it on the quotation.')
      return
    }

    if (request.type === 'reprice') {
      const matches = linesFor(request)
      if (matches.length === 0) {
        toast(`${request.item_code} is not on the quotation — nothing to reprice.`)
        return
      }
      if (matches.length > 1) {
        toast(
          `${request.item_code} is on ${matches.length} lines (${matches.map((l) => l.line_id).join(', ')}) — edit them on the quotation.`,
        )
        return
      }
      // The purchaser's new rate, entered in the row — applying the price already on the line
      // would close the request having changed nothing.
      if (newRate === undefined) return
      const line = matches[0]
      setBusyRequestId(request.id)
      // No display-currency selector on this screen (unlike QuotePage) — the typed rate is
      // already in the line's own currency, so `fx` is the identity (`FX_FALLBACK`'s empty
      // snapshot makes `convert` a no-op for a same-currency pair).
      updateLine.mutate(
        {
          lineId: line.line_id,
          cost: line.cost_unit,
          fx: FX_FALLBACK,
          patch: {
            sell_unit: { amount: newRate, currency: line.sell_unit.currency },
          },
        },
        {
          onSuccess: () => {
            setBusyRequestId(null)
            // The customer asked for a price and now has it — the request is answered, so it must
            // stop blocking the re-issue. See `stillOpen` for why this cannot be written back.
            setResolvedHere((prior) => new Set(prior).add(request.id))
            setRepriced(true)
            toast('Rate updated — apply the round below to issue it as a revision.')
          },
        },
      )
      return
    }

    // set_qty / remove: QUEUE it, same as the embedded quotation's own qty/remove controls —
    // this table is the primary way a purchaser resolves requests one at a time, and calling
    // `apply_revisions` per row here is exactly the bug that defeats the one-revision guarantee
    // (see `lib/revisionBatch.ts`'s `editFromRequest`). `queueGuarded` refuses a repeated
    // item_code the same way the reprice branch above does (I5) — this request table is another
    // door into the same batch, so it needs the same lock.
    const edit = editFromRequest(request, currentQtyOf(request))
    if (!edit) return // unreachable — 'reprice' returned above, every other type maps to an edit
    if (queueGuarded(edit)) toast('Queued — hit Apply below to send it to the customer.')
  }

  // `apply_revisions` has no supplier field, so the pick used to inform the purchaser and go
  // nowhere. It now rides on the queued add and is written onto the created line by
  // `setLineCost` (Akhil, 2026-09-16 — the PO is raised from the line).
  const pickSupplier = (supplier: string) => {
    if (!addingFor) return
    const edit = editFromRequest(addingFor, null)
    if (!edit) return
    // Guarded, not queued directly: an add that duplicates an existing line is refused here.
    if (!queueGuarded({ ...edit, supplier, source: 'Supplier' })) return
    setAddingFor(null)
    toast('Queued — hit Apply below to send it to the customer.')
  }

  /* ---------- states ---------- */

  if (enquiryQuery.isPending || quotationQuery.isPending) return <Placeholder>Loading revisions…</Placeholder>

  if (enquiryQuery.error || !enquiryQuery.data) {
    const error = enquiryQuery.error as Error | null
    return (
      <Placeholder>
        <b>Could not load {id}.</b> Check the mock backend is running (<b>npm run dev:all</b>).
        {error ? <div style={{ marginTop: 6 }}>{error.message}</div> : null}
      </Placeholder>
    )
  }

  const enquiry = enquiryQuery.data

  // Revisions only exist against a quotation.
  if (!quotation) {
    return (
      <>
        <ContextBar enquiry={enquiry} active="quote" />
        <PageHead help="revisions" title="Customer Revisions" />
        <Placeholder>
          <b>No quotation yet.</b> Revisions are changes the customer asks for after one has been sent — build it on the{' '}
          <span className="linkish" onClick={() => navigate(`/quote/${id}`)}>
            Customer Quotation
          </span>{' '}
          screen first.
        </Placeholder>
      </>
    )
  }

  /*
    The line a recorded price attaches to. `lineForRevisionRequest` returns the enquiry's own line
    when the item is already on it (keeping `cust_sno`, so the price still joins the comparison)
    and synthesizes one when the customer has just asked to ADD the item — which is the whole
    point of recording a price here, and the case the enquiry cannot supply a line for.
  */
  const recordingLines = recordingQuote
    ? lineForRevisionRequest(recordingQuote, enquiry.items, itemsByCode.get(recordingQuote.item_code ?? ''))
    : []

  return (
    <>
      <ContextBar enquiry={enquiry} active="quote" />
      {/* Every control below is disabled once the enquiry is decided — `lib/readOnly.ts`. */}
      <ReadOnlyGate reason={lockReason(enquiry.status, 'revisions')}>

      <PageHead help="revisions"
        title="Customer Revisions"
        subtitle={
          <>
            After a quotation is sent the customer may accept, revise rates, or add/remove/change items. Applying a
            revision updates the <b>Customer Quotation</b> directly. New items show their suppliers with stock &amp;
            base price.
          </>
        }
      />

      <div className="banner">
        ✏️ Revision round <b>#{revisionsQuery.data?.revision ?? quotation.revision ?? 1}</b> · <b>{open.length}</b> open
        item(s) · changes flow straight into the quotation. When adding an item, suppliers we've{' '}
        <b>quoted this item before</b> (on other requests) show their <b>past price</b> so you can decide without a
        fresh RFQ; connected suppliers with no history are listed too.
      </div>

      {revisionsQuery.error ? (
        <Placeholder>
          <b>Could not load the revision requests.</b>
          <div style={{ marginTop: 6 }}>{(revisionsQuery.error as Error).message}</div>
        </Placeholder>
      ) : revisionsQuery.isPending ? (
        <Placeholder>Loading requests…</Placeholder>
      ) : (
        <RevisionRequestsTable
          requests={requests}
          itemsByCode={itemsByCode}
          currentQtyOf={currentQtyOf}
          busyRequestId={busyRequestId}
          pending={pending}
          onApply={applyRequest}
          onDiscard={(request) => request.item_code && setBatch((b) => discardEdit(b, request.item_code as string))}
          onChooseSupplier={(request) =>
            // A request with no item_code has nothing to pick a supplier for; the modal
            // would sit on a permanently-pending query.
            request.item_code
              ? openAddDialog(request)
              : toast('This request has no item code — resolve it on the quotation.')
          }
          onResource={() => navigate(`/compare/${id}`)}
        />
      )}

      {/*
        What is queued but not yet sent. Directly under the requests table because that table says
        "No revision requests recorded" for a purchaser's OWN edits — which read as "nothing is
        happening" while a batch sat waiting.
      */}
      <PendingChangesCard
        batch={batch}
        nameByCode={new Map([...itemsByCode].map(([code, item]) => [code, item.name]))}
        currency={baseCurrencyFor(enquiry.header.company)}
        nextRevision={(revisionsQuery.data?.revision ?? 0) + 1}
        onDiscardOne={(itemCode) => setBatch((b) => discardEdit(b, itemCode))}
      />

      {/*
        The quotation itself, below the requests. The requests are the agenda; this is the thing
        they are about. Acting on "make line 3 cheaper" used to mean navigating to /quote and
        back, with the request no longer on screen.
      */}
      <QuotationEditor
        enquiryId={id}
        quotation={quotation}
        enquiry={enquiry}
        /*
          The ONLY send on this screen, and it lives in the totals panel beside the figures it is
          about — not on a second button further down, which is what made C1 possible and what
          made a purchaser open `/quote` just to press send.

          The gate travels with it: an unresolved customer request still blocks the send, stated
          in words rather than as a dead button with a tooltip.
        */
        /*
          A sent quotation's terms stay editable HERE. `/quote` locks them so a document a
          customer holds cannot be altered under them by accident; this screen is where altering
          it is the whole intent, and the history's "Not sent yet" row is what keeps the
          divergence visible until it is issued.
        */
        termsEditable
        sendLabel={`Send revision #${revisionsQuery.data?.revision ?? quotation.revision ?? 0} to customer`}
        sendBlockedReason={open.length > 0 ? `Resolve ${open.length} open request(s) first.` : null}
        structural={{
          pending,
          onQueueQty: (itemCode, qty) => queueGuarded({ item_code: itemCode, op: 'set_qty', qty }),
          onQueueRemove: (itemCode) => queueGuarded({ item_code: itemCode, op: 'remove', qty: 0 }),
          onDiscard: (itemCode) => setBatch((b) => discardEdit(b, itemCode)),
          // A price edit made directly on a line — see `StructuralEditing.onRepriced`.
          onRepriced: () => setRepriced(true),
        }}
      />

      <div className="row" style={{ marginTop: 14, flexWrap: 'wrap' }}>
        <Button onClick={() => setPickingFromMaster(true)}>✚ Add item from master</Button>
        <Button onClick={() => navigate(`/quote/${id}`)}>View quotation</Button>
        {/*
          Shown for a reprice-only round too. `update_quotation_line` bumps nothing, so without
          `repriced` a purchaser who changed five rates had no way to issue them — the button keyed
          on the structural batch alone and never appeared.
        */}
        {batch.length > 0 || repriced ? (
          <>
            <Button
              variant="primary"
              // The pricing PATCHes run AFTER the apply resolves, so the button has to stay
              // busy through both or it goes live again mid-sequence.
              disabled={applyRevisions.isPending || setLineCosts.isPending}
              onClick={() =>
                applyRevisions.mutate(toChanges(batch), {
                  onSuccess: async () => {
                    /*
                      `apply_revisions` returns no line ids, so the lines it created have to be
                      read back before their cost can be written. Refetched rather than taken
                      from the cache — the query it invalidated may not have settled yet, and a
                      stale copy would not contain the new line at all.
                    */
                    // A supplier-only add is written too — the WHO, without a cost.
                    const priced = batch.some((e) => e.op === 'add' && (e.cost !== undefined || e.supplier !== undefined))
                    if (priced) {
                      const fresh = await quotationQuery.refetch()
                      /*
                        Priced at the markup the quotation is ALREADY running at, not at bare cost.

                        `setLineCost` sending `cost_unit` alone makes the server derive
                        `sell_unit = cost_unit` (measured: cost 20 came back sell 20, margin 0), so
                        the added line was quoted at cost beside lines carrying 64% over it.
                        `prevailingSellRatio` reads that ratio off the lines themselves, because the
                        markup RATE no longer persists anywhere (`D27`).
                      */
                      const ratio = prevailingSellRatio(fresh.data?.lines ?? [])
                      /*
                        Into the QUOTATION's currency (2026-09-14). A stock or contract price is
                        in the company's, a recorded supplier price in the supplier's, and the
                        line's cost must be in the document's — the server derives the sell from
                        it. `exchange_rate` on the document is company → quotation; anything else
                        goes through the rates on file, and a price with no rate is skipped.
                      */
                      const quoteCurrency = fresh.data?.currency ?? baseCurrencyFor(enquiry.header.company)
                      const companyCurrency = baseCurrencyFor(enquiry.header.company)
                      const toQuoteCurrency = (from: string): number | null => {
                        const source = from || companyCurrency
                        if (source === quoteCurrency) return 1
                        if (source === companyCurrency) return fresh.data?.exchange_rate ?? 1
                        return pickRate(exchangeRates.data ?? [], source, quoteCurrency)?.rate ?? null
                      }
                      const rows = pricedAddsFor(batch, fresh.data?.lines ?? [], toQuoteCurrency, companyCurrency).map((row) => ({
                        ...row,
                        sell: ratio === null ? undefined : Math.round(row.cost * ratio * 100) / 100,
                      }))
                      if (rows.length > 0) {
                        await setLineCosts.mutateAsync(rows).catch(() => {
                          // Reported by the mutation's own onError; the batch still clears below,
                          // because the structural change DID apply and re-applying would double it.
                        })
                      }
                      /*
                        The customer's wording and the item name onto each added line (Akhil,
                        2026-09-15: "new added items — description not saving"). `apply_revisions`
                        leaves both blank (D29). Serialized per document, like every other write.
                      */
                      const nameByCode = new Map([...itemsByCode].map(([code, item]) => [code, item.name]))
                      for (const row of describedAddsFor(batch, fresh.data?.lines ?? [], nameByCode)) {
                        await describeLine
                          .mutateAsync({
                            lineId: row.line_id,
                            patch: {
                              ...(row.description ? { description: row.description } : {}),
                              ...(row.item_name ? { item_name: row.item_name } : {}),
                              ...(row.position ? { position: row.position } : {}),
                            },
                          })
                          .catch(() => {
                            // Reported by the mutation's own onError. The line exists and is priced;
                            // only its wording is missing, and the purchaser can type it.
                          })
                      }
                    }
                    setBatch(emptyBatch())
                    setRepriced(false)
                  },
                })
              }
            >
              {applyRevisions.isPending || setLineCosts.isPending
                ? setLineCosts.isPending
                  ? 'Pricing the added line(s)…'
                  : 'Applying…'
                : `${
                    batch.length > 0 ? describeBatch(batch) : 'Apply the new rates'
                  } — starts revision #${(revisionsQuery.data?.revision ?? 0) + 1}`}
            </Button>
            {/* Only structural edits can be discarded — a rate is already written to the
                quotation by the time it gets here, so there is nothing local to throw away. */}
            {batch.length > 0 ? (
              <Button onClick={() => setBatch(emptyBatch())}>Discard changes</Button>
            ) : null}
          </>
        ) : null}
        {quotation.status !== 'draft' ? (
          <div style={{ alignSelf: 'center', color: 'var(--muted)', fontSize: 12 }}>
            <Badge tone="ok">{quotation.status}</Badge> — apply a change to reopen it as a draft.
          </div>
        ) : null}
      </div>


      <RevisionHistory
        rounds={sentHistory.rounds}
        unsent={sentHistory.unsent}
        loading={historyQuery.isPending || sendsQuery.isPending}
        error={((historyQuery.error ?? sendsQuery.error) as Error | null) ?? null}
      />

      {pickingFromMaster ? (
        <CatalogPickerModal
          title="Add item from master — pick item"
          intro="Adds a line to this quotation directly. The customer hasn't formally requested it, so no revision request is closed."
          enquiryType={enquiry.header.enquiry_type}
          onClose={() => setPickingFromMaster(false)}
          onPick={(item) => {
            setPickingFromMaster(false)
            // Reuse the supplier modal by synthesising a request that closes nothing.
            openAddDialog({
              id: `manual-${item.code}`,
              revision_no: quotation?.revision ?? 1,
              type: 'add',
              item_code: item.code,
              qty: 1,
              detail: 'Added by the purchaser',
              status: 'open',
              raised_on: new Date().toISOString(),
            })
          }}
        />
      ) : null}

      {addingFor ? (
        <SupplierChoiceModal
          request={addingFor}
          // Picking a supplier QUEUES the add (see `pickSupplier`) — instant. Adding the line to
          // the enquiry for an RFQ is NOT: that is a real write, and a second click during it
          // would add the item twice.
          busy={addLine.isPending}
          onPick={pickSupplier}
          onClose={() => setAddingFor(null)}
          choice={addChoice}
          onChoice={(patch) => setAddChoice((c) => ({ ...c, ...patch }))}
          positions={positionOptions(quotation?.lines ?? [], batch)}
          /*
            Both routes out of "nobody has priced this". The RFQ screen is where an RFQ is
            composed and sent — this hands the purchaser there rather than building a second,
            thinner sender that could drift from it. The revision stays queued; they come back.
          */
          onSendRfq={async () => {
            const plan = planRfqSourcing(
              addingFor,
              enquiry.items,
              itemsByCode.get(addingFor.item_code ?? ''),
            )
            if (plan.kind === 'cannot') {
              toast(plan.reason)
              return
            }
            /*
              The line has to exist on the ENQUIRY before an RFQ can carry it — `RFQ Item` joins
              back on `enquiry_row_no`. Done here rather than left as an instruction: the RFQ
              builder's catalogue is ~18,000 items, and "go and find it yourself" was the whole
              defect.
            */
            if (plan.kind === 'add') {
              try {
                await addLine.mutateAsync(plan.line)
              } catch {
                // `useAddLine`'s own onError has already said what went wrong. Stay put.
                return
              }
            }
            setAddingFor(null)
            navigate(`/rfq/${id}`)
            toast(
              plan.kind === 'add'
                ? 'Added to the enquiry — assign its suppliers and send the RFQ.'
                : 'Already on the enquiry — assign its suppliers and send the RFQ.',
            )
          }}
          // Recorded right here, because a price given on the phone needs no round trip.
          onRecordQuotation={() => {
            setRecordingQuote(addingFor)
            setAddingFor(null)
          }}
          contracts={contractsForItem(rateIndex, addingFor.item_code)}
          /*
            A contract rate needs no RFQ and no recorded quotation — it is already agreed, so the
            add is queued priced, exactly as a recorded supplier price is. `pricedAddsFor` then
            writes it onto the line the apply creates.
          */
          /*
            Supplied from our own shelf. `source: 'Base Price'` is what the quotation line carries
            for an in-house line — `tagFromSource` groups it with `provision`, since it skipped the
            RFQ exactly as a contract line does.

            The figure is a COST, not a selling price: the margin is applied on the quotation, the
            same as every other line (`directPricing.ts` records the ruling and the trap).
          */
          onPickStock={(unitCost) => {
            const edit = editFromRequest(addingFor, null)
            if (!edit) return
            if (!queueGuarded({ ...edit, cost: unitCost.amount, source: 'Base Price' })) return
            setAddingFor(null)
            toast('Queued from our own stock — hit Apply below to send it to the customer.')
          }}
          onPickContract={(contract) => {
            const edit = editFromRequest(addingFor, null)
            if (!edit) return
            if (!queueGuarded({ ...edit, cost: contract.rate.amount, source: 'Provision', supplier: contract.supplier })) return
            setAddingFor(null)
            toast(
              `Queued at the contract rate from ${contract.supplier} — hit Apply below to send it to the customer.`,
            )
          }}
        />
      ) : null}

      {/*
        Record a supplier's price without leaving the revision.

        The same modal the Supplier Quotations screen uses, and deliberately so: one place decides
        what a manually recorded quotation looks like. A second implementation here would drift
        from it the first time either changed.
      */}
      {recordingQuote ? (
        <RecordQuotationModal
          /*
            ONE line — the requested item's, and nothing else. Seeded from the enquiry when the
            item is on it and synthesized when it is not; `revisionQuoteLine.ts` has the reasoning
            and the live measurement behind it.
          */
          lines={recordingLines}
          // Reached only when the request names no item at all — every other case has a line now.
          emptyHint="This request doesn't name a catalogue item, so there is nothing to price against. Match it to an item first."
          baseCurrency={baseCurrencyFor(enquiry.header.company)}
          company={enquiry.header.company}
          saving={createSupplierQuotation.isPending}
          onClose={() => setRecordingQuote(null)}
          onSave={({ supplier, currency, transactionDate, rates, remarks }) => {
            const built = buildManualQuotation({
              enquiryId: id,
              supplier,
              company: enquiry.header.company,
              currency,
              transactionDate,
              lines: recordingLines,
              rates,
              remarks,
            })
            if (!built.ok) {
              toast(built.reason)
              return
            }
            /*
              The price is not only filed against the supplier — it is QUEUED onto the add, so the
              line the apply creates arrives carrying it.

              Without this the purchaser recorded a price and then watched the item land at
              AED 0.00, and had to go and fetch it back by hand: find the item again, open its
              supplier history, "use this price", apply. `apply_revisions` sources nothing itself
              (`PendingEdit.cost` records the measurement).
            */
            const recordedCost = built.payload.items[0]?.rate
            const edit = editFromRequest(recordingQuote, null)
            const itemCode = edit?.item_code
            if (edit && recordedCost !== undefined && recordedCost > 0) {
              // Same guard as every other add — see `queueGuarded`. The supplier rides along now;
              // the quotation's name is stamped on below, once the server has issued it.
              queueGuarded({ ...edit, cost: recordedCost, cost_currency: currency, source: 'Supplier', supplier })
            }
            setRecordingQuote(null)
            createSupplierQuotation.mutate(built.payload, {
              onSuccess: (sqName) => {
                if (!itemCode) return
                setBatch((b) => b.map((e) => (e.item_code === itemCode && e.op === 'add' && e.supplier === supplier ? { ...e, supplier_quotation: sqName } : e)))
              },
            })
          }}
        />
      ) : null}
      </ReadOnlyGate>
    </>
  )
}
